import { ParallaxSection } from "./ParallaxSection";
import { InkwellGroup } from "./InkwellGroup";

const REMOTE_URL = import.meta.env.VITE_GAIA_MCP_REMOTE_URL ?? "https://gaia-mcp-remote.arechampionw.workers.dev/mcp";

const SNIPPET_LOCAL = `# Claude Desktop / CLI · stdio
{
  "mcpServers": {
    "gaia-mint": {
      "command": "npx",
      "args": ["-y", "@gaia/mcp-mint"]
    }
  }
}`;

const SNIPPET_REMOTE = `# Claude Web (claude.ai) · HTTP
{
  "mcpServers": {
    "gaia-mint": {
      "url": "${REMOTE_URL}"
    }
  }
}`;

const AGENTS = [
  { id: "claude-web", name: "Claude Web",        desc: "Works in browser. No desktop app needed." },
  { id: "claude",     name: "Claude Desktop/CLI", desc: "Skill auto-loaded; mint via MCP tool." },
  { id: "codex",      name: "Codex / Codex CLI",  desc: "Same MCP server, same skill file." },
  { id: "cursor",     name: "Cursor",             desc: "Add the MCP server in settings.json." },
  { id: "cline",      name: "Cline / Continue",   desc: "Generic MCP — discover-and-go." },
  { id: "custom",     name: "Your own agent",     desc: "Speaks MCP? You're already wired." },
];

export function McpSection() {
  return (
    <section id="mcp" className="relative px-5 md:px-10 max-w-7xl mx-auto">
      <div className="relative z-10">
        <ParallaxSection range={[0, -60]}>
          <div className="max-w-3xl mb-12">
            <div className="eyebrow mb-4"><span className="dot" />universal AI mint</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95]">
              not gated to <span className="font-serif italic text-aurora">one model.</span>
            </h2>
            <p className="mt-6 text-bone-dim text-base max-w-2xl">
              Every AI-mint MCP we've seen is bolted to one vendor. GAIA's isn't. Any agent that
              speaks MCP — plus our portable <span className="font-mono text-bone">skills/mint.md</span>{" "}
              — can mint $GAIA on its user's behalf.
            </p>
          </div>
        </ParallaxSection>

        <InkwellGroup className="grid lg:grid-cols-2 gap-6 mb-12">
          <div className="ink-card p-0 overflow-hidden">
            <div className="px-6 pt-6 pb-3 flex items-center gap-2 border-b border-white/5">
              <span className="size-3 rounded-full bg-coral" />
              <span className="size-3 rounded-full bg-sun" />
              <span className="size-3 rounded-full bg-leaf" />
              <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim">
                claude web · browser
              </span>
            </div>
            <pre className="px-6 py-6 overflow-x-auto font-mono text-sm leading-relaxed text-bone">
{SNIPPET_REMOTE}
            </pre>
          </div>

          <div className="ink-card p-0 overflow-hidden">
            <div className="px-6 pt-6 pb-3 flex items-center gap-2 border-b border-white/5">
              <span className="size-3 rounded-full bg-coral" />
              <span className="size-3 rounded-full bg-sun" />
              <span className="size-3 rounded-full bg-leaf" />
              <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim">
                desktop / cli · local
              </span>
            </div>
            <pre className="px-6 py-6 overflow-x-auto font-mono text-sm leading-relaxed text-bone">
{SNIPPET_LOCAL}
            </pre>
          </div>
        </InkwellGroup>

        <InkwellGroup className="mb-12">
          <div className="ink-card p-6 md:p-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim mb-3">how it works</div>
            <ol className="space-y-4 text-bone-dim text-sm">
              {[
                ["1", "Agent loads skills/mint.md (or its native skill loader)."],
                ["2", "Agent calls the MCP tool prepare_mint(address, count)."],
                ["3", "Server returns { to, value, data, chainId } — ready to sign."],
                ["4", "User's wallet (or paymaster) signs and submits. Done."],
              ].map(([n, s]) => (
                <li key={n} className="flex gap-4">
                  <span className="font-display text-2xl text-teal-300 shrink-0">{n}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </InkwellGroup>

        <InkwellGroup className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {AGENTS.map((a) => (
            <div key={a.id} className="ink-card p-5">
              <div className="font-display text-xl mb-1">{a.name}</div>
              <div className="text-sm text-bone-dim">{a.desc}</div>
            </div>
          ))}
        </InkwellGroup>
      </div>
    </section>
  );
}
